import { useMemo } from 'react';
import { useQuery, type ApolloError } from '@apollo/client';
import type { DocumentNode } from 'graphql';

import {
	listResourceQueryDefinitions,
	viewResourceQueryDefinitions
} from '@/providers/graphql/resourceQueryDefinitions';
import type { VectroResourceType } from '@/resources/resource-types';

type Maybe<T> = T | null | undefined;

type QueryOptions = {
	enabled?: boolean;
	fetchPolicy?: 'cache-first' | 'network-only' | 'no-cache';
	variables?: Record<string, unknown>;
};

interface BaseMeta {
	gqlQuery?: DocumentNode;
	dataPath?: string;
	totalPath?: string;
	variables?: Record<string, unknown>;
}

export interface UseListOptions {
	resource: string;
	meta?: BaseMeta;
	queryOptions?: QueryOptions;
}

export interface UseOneOptions {
	resource: string;
	id: string | number;
	meta?: BaseMeta;
	queryOptions?: QueryOptions;
}

const getValueAtPath = (
	input: Maybe<Record<string, unknown>>,
	path?: string
) => {
	if (!input) {
		return undefined;
	}

	if (!path) {
		const firstKey = Object.keys(input)[0];
		return firstKey
			? (input as Record<string, unknown>)[firstKey]
			: undefined;
	}

	return path.split('.').reduce<unknown>((acc, key) => {
		if (acc == null || typeof acc !== 'object') {
			return undefined;
		}

		return (acc as Record<string, unknown>)[key];
	}, input);
};

const normaliseCollection = (value: unknown) => {
	if (Array.isArray(value)) {
		return value;
	}

	if (value && typeof value === 'object') {
		const record = value as Record<string, unknown>;

		if (Array.isArray(record.nodes)) {
			return record.nodes;
		}

		if (Array.isArray(record.items)) {
			return record.items;
		}
	}

	return [];
};

const resolveTotal = (
	data: Maybe<Record<string, unknown>>,
	collection: unknown,
	totalPath?: string
) => {
	if (totalPath) {
		const value = getValueAtPath(data, totalPath);
		if (typeof value === 'number') {
			return value;
		}
	}

	if (collection && typeof collection === 'object') {
		const record = collection as Record<string, unknown>;

		if (typeof record.totalCount === 'number') {
			return record.totalCount;
		}

		if (typeof record.count === 'number') {
			return record.count;
		}
	}

	const list = normaliseCollection(collection);
	return list.length;
};

export const useList = <TData = unknown>({
	resource,
	meta,
	queryOptions
}: UseListOptions) => {
	const config = listResourceQueryDefinitions[resource as VectroResourceType];
	const document = meta?.gqlQuery ?? config?.document;

	if (!document) {
		console.warn(`No list query registered for resource "${resource}".`);
		return {
			data: { data: [] as TData[], total: 0 },
			isLoading: false,
			isError: false,
			error: undefined as ApolloError | undefined,
			refetch: async () => ({ data: undefined })
		};
	}

	const variables = {
		...config?.variables,
		...meta?.variables,
		...queryOptions?.variables
	} as Record<string, unknown> | undefined;

	const skip = queryOptions?.enabled === false;

	const queryResult = useQuery(document, {
		variables,
		skip,
		fetchPolicy: queryOptions?.fetchPolicy
	});

	const collection = useMemo(() => {
		return getValueAtPath(
			queryResult.data as Maybe<Record<string, unknown>>,
			meta?.dataPath ?? config?.dataPath
		);
	}, [queryResult.data, meta?.dataPath, config?.dataPath]);

	const list = useMemo(
		() => normaliseCollection(collection) as TData[],
		[collection]
	);

	const total = useMemo(
		() =>
			resolveTotal(
				queryResult.data as Maybe<Record<string, unknown>>,
				collection,
				meta?.totalPath ?? config?.totalPath
			),
		[collection, config?.totalPath, meta?.totalPath, queryResult.data]
	);

	return {
		data: { data: list, total },
		isLoading: queryResult.loading,
		isError: Boolean(queryResult.error),
		error: queryResult.error as ApolloError | undefined,
		refetch: queryResult.refetch
	};
};

export const useOne = <TData = unknown>({
	resource,
	id,
	meta,
	queryOptions
}: UseOneOptions) => {
	const config = viewResourceQueryDefinitions[resource as VectroResourceType];
	const document = meta?.gqlQuery ?? config?.document;

	if (!document) {
		console.warn(`No detail query registered for resource "${resource}".`);
		return {
			data: { data: undefined as TData | undefined },
			isLoading: false,
			isError: false,
			error: undefined as ApolloError | undefined,
			refetch: async () => ({ data: undefined })
		};
	}

	const variables = {
		id,
		...config?.variables,
		...meta?.variables,
		...queryOptions?.variables
	} as Record<string, unknown> | undefined;

	const skip = queryOptions?.enabled === false || !id;

	const queryResult = useQuery(document, {
		variables,
		skip,
		fetchPolicy: queryOptions?.fetchPolicy
	});

	const record = useMemo(() => {
		const value = getValueAtPath(
			queryResult.data as Maybe<Record<string, unknown>>,
			meta?.dataPath ?? config?.dataPath
		);

		if (Array.isArray(value)) {
			return (value as TData[])[0];
		}

		return value as TData | undefined;
	}, [queryResult.data, meta?.dataPath, config?.dataPath]);

	return {
		data: { data: record },
		isLoading: queryResult.loading,
		isError: Boolean(queryResult.error),
		error: queryResult.error as ApolloError | undefined,
		refetch: queryResult.refetch
	};
};
